import { addAttributionParams, getLastAttributionId } from "../Attribution";

const LAST_VIEWED_CLIP_KEY = "lastViewedClip";
const LAST_VIEWED_PROFILE_KEY = "lastViewedProfile";
const LAST_VIEWED_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;

export const getReferralIdFromSearch = (search: string): string | null => {
  const ref = new URLSearchParams(search).get("ref");
  return ref && OBJECT_ID_PATTERN.test(ref) ? ref.toLowerCase() : null;
};

export const getReferralInviteUrl = (
  origin: string,
  userId?: string | null,
): string =>
  userId && OBJECT_ID_PATTERN.test(userId)
    ? addAttributionParams(new URL("/invite", origin).toString(), userId)
    : "";

type ReferralVisit = { id: string; at: number };

const readVisit = (key: string, field: string): ReferralVisit | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw);
    const age = Date.now() - value.at;
    if (
      typeof value[field] !== "string" ||
      !value[field] ||
      typeof value.at !== "number" ||
      !Number.isFinite(age) ||
      age < 0 ||
      age > LAST_VIEWED_TTL_MS
    )
      return null;
    return { id: value[field], at: value.at };
  } catch (e) {
    return null;
  }
};

export const recordClipView = (shareId: string): void => {
  try {
    localStorage.setItem(
      LAST_VIEWED_CLIP_KEY,
      JSON.stringify({ shareId, at: Date.now() }),
    );
  } catch (e) {}
};

export const recordProfileVisit = (userId: string): void => {
  if (!OBJECT_ID_PATTERN.test(userId)) return;
  try {
    localStorage.setItem(
      LAST_VIEWED_PROFILE_KEY,
      JSON.stringify({ userId, at: Date.now() }),
    );
  } catch (e) {}
};

export const getLastViewedShareId = (): string | null =>
  readVisit(LAST_VIEWED_CLIP_KEY, "shareId")?.id ?? null;

export const clearLastViewedClip = (): void => {
  try {
    localStorage.removeItem(LAST_VIEWED_CLIP_KEY);
  } catch (e) {}
};

export const clearReferralVisits = (): void => {
  clearLastViewedClip();
  try {
    localStorage.removeItem(LAST_VIEWED_PROFILE_KEY);
  } catch (e) {}
};

// Explicit referral links retain priority. Otherwise the latest organic visit
// wins. Profile owners use the existing ref input, which the server validates
// against User and rejects for self-referrals. These ledger rows use REF_PARAM.
export const getReferralSignupParams = (): {
  ref?: string;
  lastViewedShareId?: string;
} => {
  const params: { ref?: string; lastViewedShareId?: string } = {};
  const ref = getLastAttributionId();
  const clip = readVisit(LAST_VIEWED_CLIP_KEY, "shareId");
  const profile = readVisit(LAST_VIEWED_PROFILE_KEY, "userId");

  if (ref && OBJECT_ID_PATTERN.test(ref)) {
    params.ref = ref;
  } else if (
    profile &&
    OBJECT_ID_PATTERN.test(profile.id) &&
    (!clip || profile.at >= clip.at)
  ) {
    params.ref = profile.id;
  }
  // Retain the clip as a server-side fallback if the ref user no longer exists.
  if (clip) params.lastViewedShareId = clip.id;
  return params;
};
